<script setup lang="ts">
import { computed, ref } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { useAuthStore } from "../stores/auth";
import SyncQueueIndicator from "./SyncQueueIndicator.vue";

const auth = useAuthStore();
const router = useRouter();
const route = useRoute();

const menuOpen = ref(false);

const navItems = computed(() => {
  if (auth.isAdmin) {
    return [
      { to: { name: "dashboard" },       label: "Dashboard" },
      { to: { name: "complaints-list" }, label: "Khiếu nại" },
      { to: { name: "complaint-new" },   label: "Tạo mới" },
      { to: { name: "admin-products" },  label: "Sản phẩm" },
      { to: { name: "admin-recalled-lots" }, label: "Lô thu hồi" },
      { to: { name: "admin-api-keys" },  label: "API Keys" },
      { to: { name: "admin-users" },     label: "Người dùng" },
    ];
  }
  // Reporter
  return [
    { to: { name: "my-complaints" }, label: "Của tôi" },
    { to: { name: "complaint-new" }, label: "Tạo mới" },
  ];
});

function isActive(name: string): boolean {
  if (name === "complaints-list") {
    return route.path.startsWith("/complaints");
  }
  if (name === "my-complaints") {
    return route.name === "my-complaints" || route.path.startsWith("/complaints");
  }
  return route.name === name;
}

// FAB hidden on pages that already have their own save CTA.
const showFab = computed<boolean>(
  () => route.name !== "complaint-new" && route.name !== "complaint-detail",
);

const bottomNavItems = computed(() => {
  if (auth.isAdmin) {
    return [
      { to: { name: "dashboard" },       label: "Dashboard", icon: "🏠" },
      { to: { name: "complaints-list" }, label: "Khiếu nại", icon: "📋" },
      { to: { name: "admin-products" },  label: "Sản phẩm",  icon: "🏷️" },
    ];
  }
  // Reporter
  return [
    { to: { name: "my-complaints" }, label: "Của tôi", icon: "📋" },
    { to: { name: "complaint-new" }, label: "Tạo mới", icon: "✏️" },
  ];
});

function openCreate(): void {
  void router.push({ name: "complaint-new" });
}

const currentTitle = computed<string>(() => {
  return (route.meta.title as string | undefined) ?? "KFF Complaints";
});

function toggleMenu(): void {
  menuOpen.value = !menuOpen.value;
}

function closeMenu(): void {
  menuOpen.value = false;
}

function logout(): void {
  auth.clearUser();
  void router.push({ name: "login" });
}
</script>

<template>
  <div class="layout">
    <header class="app-header">
      <div class="brand">
        <RouterLink :to="auth.isAdmin ? { name: 'dashboard' } : { name: 'my-complaints' }" class="brand-link" @click="closeMenu">
          <span class="brand-mark">KFF</span>
          <span class="brand-text">Complaints</span>
        </RouterLink>
      </div>

      <button
        type="button"
        class="menu-toggle"
        :aria-expanded="menuOpen"
        aria-label="Toggle navigation"
        @click="toggleMenu"
      >
        <span class="hamburger" aria-hidden="true">☰</span>
      </button>

      <nav class="app-nav" :class="{ open: menuOpen }" aria-label="Main">
        <RouterLink
          v-for="item in navItems"
          :key="item.label"
          :to="item.to"
          class="nav-link"
          :class="{ active: isActive(String(item.to.name)) }"
          @click="closeMenu"
        >
          {{ item.label }}
        </RouterLink>
      </nav>

      <div class="user-area" :class="{ open: menuOpen }">
        <span v-if="auth.email" class="user-email" :title="auth.email">
          {{ auth.email }}
        </span>
        <button type="button" class="logout-btn" @click="logout">
          Đăng xuất
        </button>
      </div>
    </header>

    <main class="app-main">
      <h1 class="page-title">{{ currentTitle }}</h1>
      <!--
        SyncQueueIndicator (P.4) — sticky banner that shows when there are
        queued offline submissions waiting to sync, when a sync is in
        progress, or after a sync completes. Mounted here so it appears
        across every AppLayout route (Dashboard, ComplaintsList, etc).
      -->
      <SyncQueueIndicator />
      <slot />
    </main>

    <footer class="app-footer">
      <small>KFF Complaints · MVP-1.9 foundation</small>
    </footer>

    <!--
      Mobile-only FAB (MVP-1.21). Hidden on /complaints/new (the page
      already has its own save CTA) and on /login (bare layout, this
      component is never mounted). Desktop users have the nav link
      "Tạo mới" + an "Tạo khiếu nại mới" CTA on the dashboard.
    -->
    <!-- Mobile bottom nav — 3 primary destinations -->
    <nav class="bottom-nav" aria-label="Main mobile navigation">
      <RouterLink
        v-for="item in bottomNavItems"
        :key="item.label"
        :to="item.to"
        class="bottom-nav-item"
        :class="{ active: isActive(String(item.to.name)) }"
      >
        <span class="bnav-icon" aria-hidden="true">{{ item.icon }}</span>
        <span class="bnav-label">{{ item.label }}</span>
      </RouterLink>
    </nav>

    <button
      v-if="showFab"
      type="button"
      class="fab-new-complaint"
      data-testid="fab-new-complaint"
      aria-label="Tạo mới"
      @click="openCreate"
    >
      <span class="fab-icon" aria-hidden="true">+</span>
      <span class="fab-text">Tạo mới</span>
    </button>
  </div>
</template>

<style scoped>
/* ── Layout shell ─────────────────────────────────────────────────── */
.layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  color: var(--color-text);
  background: var(--color-bg);
}

/* ── Top header ───────────────────────────────────────────────────── */
.app-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.75rem 1.25rem;
  background: var(--kff-forest);
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}

.brand-link {
  display: inline-flex;
  align-items: baseline;
  gap: 0.5rem;
  color: inherit;
  text-decoration: none;
  font-weight: 700;
}

.brand-mark {
  background: var(--kff-lime);
  color: #ffffff;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.95rem;
  letter-spacing: 0.06em;
  font-weight: 800;
}

.brand-text {
  font-size: 1rem;
  opacity: 0.9;
}

.app-nav {
  display: flex;
  gap: 0.25rem;
  flex: 1 1 auto;
  flex-wrap: wrap;
}

.nav-link {
  display: inline-block;
  padding: 0.4rem 0.75rem;
  color: rgba(255, 255, 255, 0.82);
  text-decoration: none;
  border-radius: 4px;
  font-size: 0.92rem;
  transition: background 0.15s ease, color 0.15s ease;
}

.nav-link:hover,
.nav-link:focus {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.nav-link.active {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-weight: 600;
}

.user-area {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-email {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.75);
  max-width: 14ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logout-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: rgba(255, 255, 255, 0.85);
  padding: 0.3rem 0.75rem;
  border-radius: 4px;
  font-size: 0.82rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.menu-toggle {
  display: none;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: rgba(255, 255, 255, 0.85);
  border-radius: 4px;
  padding: 0.25rem 0.6rem;
  font-size: 1.1rem;
  cursor: pointer;
  margin-left: auto;
}

/* ── Content area ─────────────────────────────────────────────────── */
.app-main {
  flex: 1 1 auto;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 2rem;
}

.page-title {
  font-size: 1.5rem;
  margin: 0 0 1rem;
  color: var(--kff-forest);
  font-weight: 700;
}

.app-footer {
  padding: 1rem 1.25rem;
  text-align: center;
  color: var(--kff-slate);
  font-size: 0.82rem;
}

/* ── Mobile bottom nav ────────────────────────────────────────────── */
.bottom-nav {
  display: none; /* desktop: hidden */
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 18;
  background: var(--kff-white);
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
  padding-bottom: env(safe-area-inset-bottom);
}

.bottom-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  flex: 1;
  padding: 0.55rem 0.25rem;
  color: var(--kff-slate);
  text-decoration: none;
  font-size: 0.7rem;
  font-weight: 500;
  transition: color 0.15s ease;
  min-height: 56px;
}

.bottom-nav-item:hover {
  color: var(--kff-forest);
}

.bottom-nav-item.active {
  color: var(--kff-forest);
  font-weight: 700;
  position: relative;
}

.bottom-nav-item.active::before {
  content: "";
  position: absolute;
  top: 0;
  left: 20%;
  right: 20%;
  height: 2px;
  background: var(--kff-lime);
  border-radius: 0 0 2px 2px;
}

.bnav-icon {
  font-size: 1.3rem;
  line-height: 1;
}

.bnav-label {
  white-space: nowrap;
}

/* ── FAB ──────────────────────────────────────────────────────────── */
.fab-new-complaint {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 19;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 48px;
  min-height: 48px;
  padding: 0.65rem 1rem;
  border: none;
  border-radius: 24px;
  background: var(--kff-lime);
  color: #ffffff;
  font: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(115, 184, 67, 0.45);
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}

.fab-new-complaint:hover {
  background: var(--kff-lime-dark);
  box-shadow: 0 5px 14px rgba(115, 184, 67, 0.55);
}

.fab-new-complaint:active {
  transform: scale(0.96);
  background: var(--kff-lime-dark);
}

.fab-new-complaint:focus-visible {
  outline: 3px solid var(--kff-forest);
  outline-offset: 2px;
}

.fab-icon {
  display: inline-block;
  font-size: 1.3rem;
  font-weight: 400;
  line-height: 1;
}

.fab-text {
  white-space: nowrap;
}

/* ── Mobile ≤720px ────────────────────────────────────────────────── */
@media (max-width: 720px) {
  .app-header {
    align-items: stretch;
  }

  .menu-toggle {
    display: inline-block;
  }

  .app-nav,
  .user-area {
    display: none;
    flex-basis: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 0.25rem;
    margin-top: 0.5rem;
  }

  .app-nav.open,
  .user-area.open {
    display: flex;
  }

  .user-area.open {
    border-top: 1px solid rgba(255, 255, 255, 0.18);
    padding-top: 0.5rem;
  }

  .nav-link {
    padding: 0.6rem 0.75rem;
  }

  .user-email {
    max-width: none;
  }

  /* Show bottom nav */
  .bottom-nav {
    display: flex;
  }

  /* Main content clears the bottom nav (56px) + safe area */
  .app-main {
    padding-bottom: calc(56px + env(safe-area-inset-bottom) + 1rem);
  }

  /* FAB sits above the bottom nav */
  .fab-new-complaint {
    bottom: calc(56px + env(safe-area-inset-bottom) + 0.75rem);
  }
}

/* ≤360px: icon-only FAB */
@media (max-width: 360px) {
  .fab-new-complaint .fab-text {
    display: none;
  }
  .fab-new-complaint {
    border-radius: 50%;
    padding-left: 0.65rem;
    padding-right: 0.65rem;
  }
}
</style>